// // ─── Printable Slip Document ─────────────────────────────────────────────
// // Builds the Salary Slip / Attendance / History pages as a completely
// // standalone HTML document — its own <html>, its own <style>, no
// // dependency on the app's layout, sidebar, header, or CSS modules.
// //
// // Instead of calling window.print() on the current page (which prints
// // whatever else is on screen unless every element is hidden with CSS),
// // this opens a *new* blank window, writes a self-contained document into
// // it, and prints only that. Closing the window afterwards is automatic.
// //
// // Usage (e.g. from StaffDetailModal's "Print" button):
// //
// //   import { openPrintableSlip } from "./PrintableSlipDocument";
// //
// //   const handlePrint = () => {
// //     openPrintableSlip({ employee, records, year, month, history, companyName });
// //   };

// import { MONTH_NAMES, formatDateTime } from "./HrmData";
// import {
//   buildPrintSalarySlip,
//   buildMonthGridPrint,
//   PRINT_WEEK_HEADS,
//   STATUS_SHORT,
//   STATUS_LEGEND,
// } from "./HrmPrintHelpers";

// const esc = (value) =>
//   String(value ?? "").replace(
//     /[&<>"']/g,
//     (c) =>
//       ({
//         "&": "&amp;",
//         "<": "&lt;",
//         ">": "&gt;",
//         '"': "&quot;",
//         "'": "&#39;",
//       })[c],
//   );

// const inr = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN")}`;

// // ─── The document's own stylesheet — nothing borrowed from the app ────────
// const DOCUMENT_STYLES = `
//   * { box-sizing: border-box; }
//   html, body {
//     margin: 0;
//     padding: 0;
//     background: #f2f3f5;
//     font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
//     color: #1b1d21;
//   }
//   .page {
//     background: #ffffff;
//     max-width: 820px;
//     margin: 24px auto;
//     padding: 40px 44px 56px;
//   }
//   .brand { font-size: 20px; font-weight: 700; }
//   .rule { border: none; border-top: 1px solid #d9dce2; margin: 12px 0 24px; }
//   .title { font-size: 21px; font-weight: 700; margin: 0 0 18px; }
//   .section-title { font-size: 16px; font-weight: 700; margin: 26px 0 10px; }
//   table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 6px; }
//   th, td { border: 1px solid #d9dce2; padding: 11px 15px; text-align: left; vertical-align: top; }
//   thead th { background: #f2f3f6; font-size: 12px; font-weight: 700; }
//   .amount { text-align: right; white-space: nowrap; }
//   .total-row td { font-weight: 700; }
//   .label-small { font-size: 11px; font-weight: 700; opacity: 0.6; margin-bottom: 3px; }
//   .info-name { font-size: 15px; font-weight: 700; }
//   .summary-grid td { width: 25%; font-weight: 600; }
//   .cal-table { table-layout: fixed; }
//   .cal-table th { text-align: center; font-size: 11px; }
//   .cal-cell { height: 76px; }
//   .cal-date { font-size: 11px; font-weight: 600; opacity: 0.7; }
//   .cal-status { font-size: 12.5px; font-weight: 700; margin-top: 4px; }
//   .cal-empty { background: #fafafa; }
//   .legend { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 16px; font-size: 11px; }
//   .legend-item { display: inline-flex; align-items: center; gap: 6px; }
//   .legend-code {
//     display: inline-flex; align-items: center; justify-content: center;
//     min-width: 20px; height: 18px; padding: 0 4px;
//     border: 1px solid #d9dce2; border-radius: 4px;
//     font-size: 10px; font-weight: 700;
//   }
//   .history-empty { font-size: 12px; opacity: 0.6; padding: 12px 0; }
//   .toolbar {
//     max-width: 820px; margin: 16px auto 0; padding: 0 44px;
//     display: flex; gap: 10px;
//   }
//   .toolbar button {
//     font-size: 13px; font-weight: 600; padding: 9px 18px;
//     border-radius: 6px; border: 1px solid #cccccc; background: #ffffff; cursor: pointer;
//   }
//   .toolbar .primary { background: #31974D; border-color: #31974D; color: #ffffff; }

//   @media print {
//     html, body { background: #ffffff; }
//     .toolbar { display: none; }
//     .page { margin: 0 auto; max-width: none; padding: 6mm 0; box-shadow: none; }
//     .page + .page { break-before: page; page-break-before: always; }
//   }
//   @page { size: A4; margin: 14mm 12mm; }
// `;

// const buildSalaryPage = (companyName, printSlip) => `
//   <section class="page">
//     <div class="brand">${esc(companyName)}</div>
//     <hr class="rule" />
//     <h1 class="title">Salary Slip (${esc(printSlip.dateRangeLabel)})</h1>

//     <table>
//       <tr>
//         <td><div class="info-name">${esc(printSlip.employee.name)}</div></td>
//         <td>
//           <div class="label-small">Phone No</div>
//           <div>${esc(printSlip.employee.mobile || "-")}</div>
//         </td>
//         <td>
//           <div class="label-small">Monthly Gross Salary</div>
//           <div>${inr(printSlip.employee.monthlySalary)}</div>
//         </td>
//       </tr>
//     </table>

//     <h2 class="section-title">Payment &amp; Salary (${esc(printSlip.dateRangeLabel)})</h2>
//     <table>
//       <thead>
//         <tr><th>Earnings</th><th>Activity Date</th><th class="amount">Amount</th></tr>
//       </thead>
//       <tbody>
//         <tr>
//           <td>${esc(printSlip.earningsLabel)}</td>
//           <td>--</td>
//           <td class="amount">${inr(printSlip.netPay)}</td>
//         </tr>
//         <tr class="total-row">
//           <td colspan="2">Total Earnings</td>
//           <td class="amount">${inr(printSlip.netPay)}</td>
//         </tr>
//       </tbody>
//     </table>

//     <table>
//       <tr>
//         <td>Previous Month Closing Balance</td>
//         <td></td>
//         <td class="amount">${inr(printSlip.previousBalance)}</td>
//       </tr>
//       <tr class="total-row">
//         <td>Net Payable (Earnings + Previous Balance)</td>
//         <td>${esc(printSlip.netPayableWords)}</td>
//         <td class="amount">${inr(printSlip.netPayable)}</td>
//       </tr>
//     </table>

//     <h2 class="section-title">Attendance Summary (${esc(printSlip.dateRangeLabel)})</h2>
//     <table class="summary-grid">
//       <tr>
//         <td>Present - ${printSlip.counts.Present}</td>
//         <td>Absent - ${printSlip.counts.Absent}</td>
//         <td>Half Day - ${printSlip.counts["Half-Day"]}</td>
//         <td>Not Marked - ${printSlip.unmarkedDays}</td>
//       </tr>
//       <tr>
//         <td>Overtime - ${printSlip.overtimeMins} Mins</td>
//         <td>Fine - ${printSlip.fineMins} Mins</td>
//         <td>Leaves - ${printSlip.counts.Leave}</td>
//         <td>Payable Days - ${printSlip.payableDays}</td>
//       </tr>
//     </table>
//   </section>
// `;

// const buildAttendancePage = (companyName, statusByDate, gridRows, month) => {
//   const rows = gridRows
//     .map((row) => {
//       const cells = row
//         .map((date) => {
//           if (!date) return `<td class="cal-cell cal-empty"></td>`;
//           const status = statusByDate[date];
//           const dayNum = date.slice(-2);
//           const monthShort = MONTH_NAMES[month].slice(0, 3);
//           return `
//             <td class="cal-cell">
//               <div class="cal-date">${dayNum} ${monthShort}</div>
//               <div class="cal-status">${status ? esc(STATUS_SHORT[status] || status) : "&mdash;"}</div>
//             </td>`;
//         })
//         .join("");
//       return `<tr>${cells}</tr>`;
//     })
//     .join("");

//   const legend = STATUS_LEGEND.map(
//     (l) => `
//       <span class="legend-item">
//         <span class="legend-code">${esc(l.code)}</span>${esc(l.label)}
//       </span>`,
//   ).join("");

//   return `
//     <section class="page">
//       <div class="brand">${esc(companyName)}</div>
//       <hr class="rule" />
//       <table class="cal-table">
//         <thead><tr>${PRINT_WEEK_HEADS.map((d) => `<th>${d}</th>`).join("")}</tr></thead>
//         <tbody>${rows}</tbody>
//       </table>
//       <div class="legend">${legend}</div>
//     </section>
//   `;
// };

// const buildHistoryPage = (companyName, employeeName, employeeHistory) => {
//   const body = employeeHistory.length
//     ? `
//       <table>
//         <thead><tr><th>Change</th><th class="amount">When</th></tr></thead>
//         <tbody>
//           ${employeeHistory
//             .map(
//               (entry) => `
//             <tr>
//               <td>${esc(entry.description)}</td>
//               <td class="amount">${esc(formatDateTime(entry.timestamp))}</td>
//             </tr>`,
//             )
//             .join("")}
//         </tbody>
//       </table>`
//     : `<p class="history-empty">No edits recorded yet.</p>`;

//   return `
//     <section class="page">
//       <div class="brand">${esc(companyName)}</div>
//       <hr class="rule" />
//       <h1 class="title">Edit History &mdash; ${esc(employeeName)}</h1>
//       ${body}
//     </section>
//   `;
// };

// /**
//  * Builds the full standalone HTML document (string) for one employee's
//  * Salary Slip + Attendance calendar + History, for the given month.
//  */
// export const buildPrintableSlipHTML = ({
//   employee,
//   records,
//   year,
//   month,
//   history = [],
//   companyName = "Your Company",
// }) => {
//   const printSlip = buildPrintSalarySlip(employee, records, year, month);
//   printSlip.employee = employee; // keep name/mobile/salary alongside the slip numbers

//   const statusByDate = {};
//   records
//     .filter((r) => r.employeeId === employee.id)
//     .forEach((r) => (statusByDate[r.date] = r.status));

//   const gridRows = buildMonthGridPrint(year, month);

//   const employeeHistory = history
//     .filter((h) => h.employeeId === employee.id)
//     .slice()
//     .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

//   return `<!DOCTYPE html>
// <html lang="en">
// <head>
// <meta charset="UTF-8" />
// <title>Salary Slip - ${esc(employee.name)} - ${esc(MONTH_NAMES[month])} ${year}</title>
// <style>${DOCUMENT_STYLES}</style>
// </head>
// <body>
//   <div class="toolbar">
//     <button class="primary" onclick="window.print()">Print / Save as PDF</button>
//     <button onclick="window.close()">Close</button>
//   </div>
//   ${buildSalaryPage(companyName, printSlip)}
//   ${buildAttendancePage(companyName, statusByDate, gridRows, month)}
//   ${buildHistoryPage(companyName, employee.name, employeeHistory)}
// </body>
// </html>`;
// };

// /**
//  * Opens the slip document in a brand new browser window/tab — completely
//  * isolated from the app's own DOM and CSS — and triggers print.
//  * Returns the window reference (or null if the browser blocked the popup).
//  */
// export const openPrintableSlip = (params) => {
//   const html = buildPrintableSlipHTML(params);
//   const printWindow = window.open("", "_blank", "width=900,height=1000");

//   if (!printWindow) {
//     // Popup blocked — let the caller show a toast/alert if it wants.
//     return null;
//   }

//   printWindow.document.open();
//   printWindow.document.write(html);
//   printWindow.document.close();

//   printWindow.onload = () => {
//     printWindow.focus();
//     printWindow.print();
//   };

//   return printWindow;
// };

// ─── Printable Slip Document ─────────────────────────────────────────────
// Builds the Salary Slip / Attendance / History pages as a completely
// standalone HTML document — its own <html>, its own <style>, no
// dependency on the app's layout, sidebar, header, or CSS modules.
//
// The generated document opens in a brand new window and carries its own
// toolbar with:
//   - A4 / A5 page-size toggle (switches the @page rule used when printing,
//     and rescales the on-screen preview to match)
//   - "Print Full Report" — all three sections, each on its own page
//   - "Print Salary Slip" / "Print Attendance" / "Print History" — prints
//     just that one section (no page breaks, no other sections included)
//
// Usage (e.g. from StaffDetailModal's print buttons):
//
//   import { openPrintableSlip } from "./PrintableSlipDocument";
//
//   openPrintableSlip({ employee, records, year, month, history, companyName });
//   openPrintableSlip({ ... }, { autoPrint: "salary" });      // jumps straight to print
//   openPrintableSlip({ ... }, { pageSize: "A5" });           // opens defaulted to A5

import { MONTH_NAMES, formatDateTime } from "./HrmData";
import {
  buildPrintSalarySlip,
  buildMonthGridPrint,
  PRINT_WEEK_HEADS,
  STATUS_SHORT,
  STATUS_LEGEND,
} from "./HrmPrintHelpers";

const esc = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );

const inr = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN")}`;

// ─── Per-page-size @page rules — swapped in/out by setPageSize() at print time
const PAGE_CSS = {
  A4: "@page { size: A4; margin: 14mm 12mm; }",
  A5: "@page { size: A5; margin: 9mm 7mm; }",
};

// ─── The document's own stylesheet — nothing borrowed from the app ────────
const DOCUMENT_STYLES = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #f2f3f5;
    font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #1b1d21;
  }
  .toolbar {
    max-width: 900px; margin: 16px auto 0; padding: 0 24px;
    display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
  }
  .toolbar button {
    font-size: 13px; font-weight: 600; padding: 9px 16px;
    border-radius: 6px; border: 1px solid #cccccc; background: #ffffff; cursor: pointer;
  }
  .toolbar .primary { background: #31974D; border-color: #31974D; color: #ffffff; }
  .toolbar .ghost { background: transparent; }
  .toolbar .spacer { flex: 1 1 auto; }
  .size-toggle {
    display: inline-flex; align-items: center; gap: 4px;
    border: 1px solid #cccccc; border-radius: 6px; padding: 3px; background: #ffffff;
  }
  .size-toggle label {
    font-size: 12px; font-weight: 600; padding: 6px 12px; border-radius: 4px; cursor: pointer;
  }
  .size-toggle input { display: none; }
  .size-toggle label:has(input:checked) { background: #4164E3; color: #ffffff; }

  /* ---- page shell, on-screen preview ---- */
  .page {
    background: #ffffff;
    margin: 24px auto;
    padding: 40px 44px 56px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  }
  body[data-page-size="A4"] .page { max-width: 794px; }
  body[data-page-size="A5"] .page { max-width: 559px; padding: 26px 28px 36px; font-size: 12.5px; }
  body[data-page-size="A5"] .title { font-size: 17px; }
  body[data-page-size="A5"] .section-title { font-size: 13.5px; margin: 18px 0 8px; }
  body[data-page-size="A5"] th, body[data-page-size="A5"] td { padding: 7px 9px; }
  body[data-page-size="A5"] .cal-cell { height: 54px; }
  body[data-page-size="A5"] .cal-date { font-size: 9.5px; }
  body[data-page-size="A5"] .cal-status { font-size: 10.5px; }

  .brand { font-size: 20px; font-weight: 700; }
  .rule { border: none; border-top: 1px solid #d9dce2; margin: 12px 0 24px; }
  .title { font-size: 21px; font-weight: 700; margin: 0 0 18px; }
  .section-title { font-size: 16px; font-weight: 700; margin: 26px 0 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 6px; }
  th, td { border: 1px solid #d9dce2; padding: 11px 15px; text-align: left; vertical-align: top; }
  thead th { background: #f2f3f6; font-size: 12px; font-weight: 700; }
  .amount { text-align: right; white-space: nowrap; }
  .total-row td { font-weight: 700; }
  .label-small { font-size: 11px; font-weight: 700; opacity: 0.6; margin-bottom: 3px; }
  .info-name { font-size: 15px; font-weight: 700; }
  .summary-grid td { width: 25%; font-weight: 600; }
  .cal-table { table-layout: fixed; }
  .cal-table th { text-align: center; font-size: 11px; }
  .cal-cell { height: 76px; }
  .cal-date { font-size: 11px; font-weight: 600; opacity: 0.7; }
  .cal-status { font-size: 12.5px; font-weight: 700; margin-top: 4px; }
  .cal-empty { background: #fafafa; }
  .legend { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 16px; font-size: 11px; }
  .legend-item { display: inline-flex; align-items: center; gap: 6px; }
  .legend-code {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 20px; height: 18px; padding: 0 4px;
    border: 1px solid #d9dce2; border-radius: 4px;
    font-size: 10px; font-weight: 700;
  }
  .history-empty { font-size: 12px; opacity: 0.6; padding: 12px 0; }

  @media print {
    html, body { background: #ffffff; }
    .toolbar { display: none; }
    .page {
      margin: 0 auto; max-width: none !important; padding: 6mm 0;
      box-shadow: none; font-size: 13px !important;
    }
    /* Only rendered (non-hidden) sections participate in page breaks, so this
       is a no-op when a single section is being printed on its own. */
    .print-section + .print-section { break-before: page; page-break-before: always; }

    /* "Print Salary Slip / Attendance / History" — hide the other two */
    body[data-print-only="salary"] .print-section:not([data-key="salary"]) { display: none; }
    body[data-print-only="attendance"] .print-section:not([data-key="attendance"]) { display: none; }
    body[data-print-only="history"] .print-section:not([data-key="history"]) { display: none; }
  }
`;

const buildSalaryPage = (companyName, printSlip) => `
  <section class="page print-section" data-key="salary">
    <div class="brand">${esc(companyName)}</div>
    <hr class="rule" />
    <h1 class="title">Salary Slip (${esc(printSlip.dateRangeLabel)})</h1>

    <table>
      <tr>
        <td><div class="info-name">${esc(printSlip.employee.name)}</div></td>
        <td>
          <div class="label-small">Phone No</div>
          <div>${esc(printSlip.employee.mobile || "-")}</div>
        </td>
        <td>
          <div class="label-small">Monthly Gross Salary</div>
          <div>${inr(printSlip.employee.monthlySalary)}</div>
        </td>
      </tr>
    </table>

    <h2 class="section-title">Payment &amp; Salary (${esc(printSlip.dateRangeLabel)})</h2>
    <table>
      <thead>
        <tr><th>Earnings</th><th>Activity Date</th><th class="amount">Amount</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>${esc(printSlip.earningsLabel)}</td>
          <td>--</td>
          <td class="amount">${inr(printSlip.netPay)}</td>
        </tr>
        <tr class="total-row">
          <td colspan="2">Total Earnings</td>
          <td class="amount">${inr(printSlip.netPay)}</td>
        </tr>
      </tbody>
    </table>

    <table>
      <tr>
        <td>Previous Month Closing Balance</td>
        <td></td>
        <td class="amount">${inr(printSlip.previousBalance)}</td>
      </tr>
      <tr class="total-row">
        <td>Net Payable (Earnings + Previous Balance)</td>
        <td>${esc(printSlip.netPayableWords)}</td>
        <td class="amount">${inr(printSlip.netPayable)}</td>
      </tr>
    </table>

    <h2 class="section-title">Attendance Summary (${esc(printSlip.dateRangeLabel)})</h2>
    <table class="summary-grid">
      <tr>
        <td>Present - ${printSlip.counts.Present}</td>
        <td>Absent - ${printSlip.counts.Absent}</td>
        <td>Half Day - ${printSlip.counts["Half-Day"]}</td>
        <td>Not Marked - ${printSlip.unmarkedDays}</td>
      </tr>
      <tr>
        <td>Overtime - ${printSlip.overtimeMins} Mins</td>
        <td>Fine - ${printSlip.fineMins} Mins</td>
        <td>Leaves - ${printSlip.counts.Leave}</td>
        <td>Payable Days - ${printSlip.payableDays}</td>
      </tr>
    </table>
  </section>
`;

const buildAttendancePage = (companyName, statusByDate, gridRows, month) => {
  const rows = gridRows
    .map((row) => {
      const cells = row
        .map((date) => {
          if (!date) return `<td class="cal-cell cal-empty"></td>`;
          const status = statusByDate[date];
          const dayNum = date.slice(-2);
          const monthShort = MONTH_NAMES[month].slice(0, 3);
          return `
            <td class="cal-cell">
              <div class="cal-date">${dayNum} ${monthShort}</div>
              <div class="cal-status">${status ? esc(STATUS_SHORT[status] || status) : "&mdash;"}</div>
            </td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const legend = STATUS_LEGEND.map(
    (l) => `
      <span class="legend-item">
        <span class="legend-code">${esc(l.code)}</span>${esc(l.label)}
      </span>`,
  ).join("");

  return `
    <section class="page print-section" data-key="attendance">
      <div class="brand">${esc(companyName)}</div>
      <hr class="rule" />
      <table class="cal-table">
        <thead><tr>${PRINT_WEEK_HEADS.map((d) => `<th>${d}</th>`).join("")}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="legend">${legend}</div>
    </section>
  `;
};

const buildHistoryPage = (companyName, employeeName, employeeHistory) => {
  const body = employeeHistory.length
    ? `
      <table>
        <thead><tr><th>Change</th><th class="amount">When</th></tr></thead>
        <tbody>
          ${employeeHistory
            .map(
              (entry) => `
            <tr>
              <td>${esc(entry.description)}</td>
              <td class="amount">${esc(formatDateTime(entry.timestamp))}</td>
            </tr>`,
            )
            .join("")}
        </tbody>
      </table>`
    : `<p class="history-empty">No edits recorded yet.</p>`;

  return `
    <section class="page print-section" data-key="history">
      <div class="brand">${esc(companyName)}</div>
      <hr class="rule" />
      <h1 class="title">Edit History &mdash; ${esc(employeeName)}</h1>
      ${body}
    </section>
  `;
};

// ─── Toolbar + controller script embedded in the generated document ───────
const buildToolbar = (defaultSize) => `
  <div class="toolbar">
    <div class="size-toggle">
      <label><input type="radio" name="pageSize" value="A4" ${defaultSize === "A4" ? "checked" : ""} onchange="setPageSize('A4')"><span>A4</span></label>
      <label><input type="radio" name="pageSize" value="A5" ${defaultSize === "A5" ? "checked" : ""} onchange="setPageSize('A5')"><span>A5</span></label>
    </div>
    <button class="primary" onclick="printOnly('all')">Print Full Report</button>
    <button onclick="printOnly('salary')">Print Salary Slip</button>
    <button onclick="printOnly('attendance')">Print Attendance</button>
    <button onclick="printOnly('history')">Print History</button>
    <div class="spacer"></div>
    <button class="ghost" onclick="window.close()">Close</button>
  </div>
`;

const CONTROLLER_SCRIPT = `
  const PAGE_CSS = ${JSON.stringify(PAGE_CSS)};
  function setPageSize(size) {
    document.body.setAttribute('data-page-size', size);
    const styleTag = document.getElementById('page-size-rule');
    if (styleTag) styleTag.textContent = PAGE_CSS[size] || PAGE_CSS.A4;
  }
  function printOnly(key) {
    document.body.setAttribute('data-print-only', key);
    window.print();
  }
  window.addEventListener('afterprint', function () {
    document.body.removeAttribute('data-print-only');
  });
`;

/**
 * Builds the full standalone HTML document (string) for one employee's
 * Salary Slip + Attendance calendar + History, for the given month.
 */
export const buildPrintableSlipHTML = ({
  employee,
  records,
  year,
  month,
  history = [],
  companyName = "Wallicon Pvt. Ltd.",
  pageSize = "A4",
}) => {
  const printSlip = buildPrintSalarySlip(employee, records, year, month);
  printSlip.employee = employee; // keep name/mobile/salary alongside the slip numbers

  const statusByDate = {};
  records
    .filter((r) => r.employeeId === employee.id)
    .forEach((r) => (statusByDate[r.date] = r.status));

  const gridRows = buildMonthGridPrint(year, month);

  const employeeHistory = history
    .filter((h) => h.employeeId === employee.id)
    .slice()
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const size = pageSize === "A5" ? "A5" : "A4";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>Salary Slip - ${esc(employee.name)} - ${esc(MONTH_NAMES[month])} ${year}</title>
<style>${DOCUMENT_STYLES}</style>
<style id="page-size-rule">${PAGE_CSS[size]}</style>
</head>
<body data-page-size="${size}">
  ${buildToolbar(size)}
  ${buildSalaryPage(companyName, printSlip)}
  ${buildAttendancePage(companyName, statusByDate, gridRows, month)}
  ${buildHistoryPage(companyName, employee.name, employeeHistory)}
  <script>${CONTROLLER_SCRIPT}</script>
</body>
</html>`;
};

/**
 * Opens the slip document in a brand new browser window/tab — completely
 * isolated from the app's own DOM and CSS.
 *
 * `options.autoPrint` — "all" | "salary" | "attendance" | "history" — if
 * set, immediately triggers that print as soon as the window loads (handy
 * for wiring individual "Print Salary Slip" / "Print Attendance" buttons
 * directly in the app, instead of making the person click again inside the
 * popup). Leave it unset to just open the document and let the person pick
 * from the toolbar.
 *
 * `options.pageSize` — "A4" (default) | "A5" — which size the toolbar and
 * @page rule start on; the person can still switch it inside the popup.
 *
 * Returns the window reference (or null if the browser blocked the popup).
 */
export const openPrintableSlip = (params, options = {}) => {
  const { autoPrint = null, pageSize = "A4" } = options;
  const html = buildPrintableSlipHTML({ ...params, pageSize });
  const printWindow = window.open("", "_blank", "width=960,height=1040");

  if (!printWindow) {
    // Popup blocked — let the caller show a toast/alert if it wants.
    return null;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  if (autoPrint) {
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.printOnly(autoPrint);
    };
  }

  return printWindow;
};

// ─── Printable Slip Document ─────────────────────────────────────────────
// Builds the Salary Slip / Attendance / History pages as a completely
// standalone HTML document — its own <html>, its own <style>, no
// dependency on the app's layout, sidebar, header, or CSS modules.

// The generated document opens in a brand new window and carries its own
// toolbar with:
//   - A4 / A5 page-size toggle (switches the @page rule used when printing,
//     and rescales the on-screen preview to match)
//   - "Print Full Report" — all three sections, each on its own page
//   - "Print Salary Slip" / "Print Attendance" / "Print History" — prints
//     just that one section (no page breaks, no other sections included)

// The Attendance page renders as an actual calendar grid — rounded day
// cells, muted previous/next-month days, weekday header row — rather than
// a plain table, and every page uses compact (small-text) typography so a
// full month comfortably fits without feeling cramped.

// Usage (e.g. from StaffDetailModal's print buttons):

//   import { openPrintableSlip } from "./PrintableSlipDocument";

//   openPrintableSlip({ employee, records, year, month, history, companyName });
//   openPrintableSlip({ ... }, { autoPrint: "salary" });      // jumps straight to print
//   openPrintableSlip({ ... }, { pageSize: "A5" });           // opens defaulted to A5

// import { MONTH_NAMES, formatDateTime } from "./HrmData";
// import {
//   buildPrintSalarySlip,
//   buildMonthGridWithAdjacent,
//   STATUS_LEGEND,
// } from "./HrmPrintHelpers";

// const esc = (value) =>
//   String(value ?? "").replace(
//     /[&<>"']/g,
//     (c) =>
//       ({
//         "&": "&amp;",
//         "<": "&lt;",
//         ">": "&gt;",
//         '"': "&quot;",
//         "'": "&#39;",
//       })[c],
//   );

// const inr = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN")}`;

// // Short status codes shown inside each calendar day cell.
// const STATUS_CODE = {
//   Present: "P",
//   Absent: "A",
//   "Half-Day": "HD",
//   Leave: "L",
// };

// // ─── Per-page-size @page rules — swapped in/out by setPageSize() at print time
// const PAGE_CSS = {
//   A4: "@page { size: A4; margin: 12mm 10mm; }",
//   A5: "@page { size: A5; margin: 8mm 6mm; }",
// };

// // ─── The document's own stylesheet — nothing borrowed from the app ────────
// // Small-text throughout: this is meant to read like a compact printed slip,
// // not a full-size on-screen page.
// const DOCUMENT_STYLES = `
//   * { box-sizing: border-box; }
//   html, body {
//     margin: 0;
//     padding: 0;
//     background: #f2f3f5;
//     font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
//     color: #1b1d21;
//     font-size: 11px;
//   }
//   .toolbar {
//     max-width: 900px; margin: 16px auto 0; padding: 0 24px;
//     display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
//     font-size: 13px;
//   }
//   .toolbar button {
//     font-size: 12px; font-weight: 600; padding: 8px 14px;
//     border-radius: 6px; border: 1px solid #cccccc; background: #ffffff; cursor: pointer;
//   }
//   .toolbar .primary { background: #31974D; border-color: #31974D; color: #ffffff; }
//   .toolbar .ghost { background: transparent; }
//   .toolbar .spacer { flex: 1 1 auto; }
//   .size-toggle {
//     display: inline-flex; align-items: center; gap: 4px;
//     border: 1px solid #cccccc; border-radius: 6px; padding: 3px; background: #ffffff;
//   }
//   .size-toggle label {
//     font-size: 11px; font-weight: 600; padding: 6px 11px; border-radius: 4px; cursor: pointer;
//   }
//   .size-toggle input { display: none; }
//   .size-toggle label:has(input:checked) { background: #4164E3; color: #ffffff; }

//   /* ---- page shell, on-screen preview ---- */
//   .page {
//     background: #ffffff;
//     margin: 20px auto;
//     padding: 26px 30px 36px;
//     box-shadow: 0 1px 4px rgba(0,0,0,0.08);
//   }
//   body[data-page-size="A4"] .page { max-width: 720px; }
//   body[data-page-size="A5"] .page { max-width: 500px; padding: 18px 20px 26px; font-size: 10px; }
//   body[data-page-size="A5"] .title { font-size: 14px; }
//   body[data-page-size="A5"] .section-title { font-size: 11.5px; margin: 14px 0 6px; }
//   body[data-page-size="A5"] th, body[data-page-size="A5"] td { padding: 5px 7px; }
//   body[data-page-size="A5"] .cal-cell { height: 40px; }
//   body[data-page-size="A5"] .cal-day { font-size: 9px; }
//   body[data-page-size="A5"] .cal-status { font-size: 8px; }

//   .brand { font-size: 15px; font-weight: 700; }
//   .rule { border: none; border-top: 1px solid #d9dce2; margin: 8px 0 16px; }
//   .title { font-size: 16px; font-weight: 700; margin: 0 0 12px; }
//   .section-title { font-size: 12.5px; font-weight: 700; margin: 18px 0 7px; }
//   table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 4px; }
//   th, td { border: 1px solid #d9dce2; padding: 7px 10px; text-align: left; vertical-align: top; }
//   thead th { background: #f2f3f6; font-size: 10px; font-weight: 700; }
//   .amount { text-align: right; white-space: nowrap; }
//   .total-row td { font-weight: 700; }
//   .label-small { font-size: 9px; font-weight: 700; opacity: 0.6; margin-bottom: 2px; }
//   .info-name { font-size: 12.5px; font-weight: 700; }
//   .summary-grid td { width: 25%; font-weight: 600; }
//   .history-empty { font-size: 10.5px; opacity: 0.6; padding: 10px 0; }

//   /* ---- calendar (date-picker style) ---- */
//   .cal-head-row { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 10px; }
//   .cal-month-label { font-size: 13px; font-weight: 700; }
//   .cal-employee { font-size: 10px; opacity: 0.65; }
//   .cal-weekdays, .cal-grid {
//     display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px;
//   }
//   .cal-weekday {
//     text-align: center; font-size: 9px; font-weight: 700; opacity: 0.55;
//     padding-bottom: 4px; text-transform: uppercase; letter-spacing: 0.02em;
//   }
//   .cal-cell {
//     height: 48px;
//     border: 1px solid #e2e6ee;
//     border-radius: 8px;
//     padding: 4px 5px;
//     display: flex;
//     flex-direction: column;
//     justify-content: flex-start;
//   }
//   .cal-cell.out-month { background: #fafafa; border-color: #eef0f3; }
//   .cal-cell.out-month .cal-day { opacity: 0.35; }
//   .cal-day { font-size: 10px; font-weight: 700; }
//   .cal-status {
//     margin-top: auto;
//     font-size: 8.5px;
//     font-weight: 700;
//     border-radius: 4px;
//     padding: 1px 4px;
//     display: inline-block;
//     width: fit-content;
//   }
//   .cal-status.st-present { background: #e6f6ea; color: #1e7a3a; }
//   .cal-status.st-absent  { background: #fdeaea; color: #c62828; }
//   .cal-status.st-half    { background: #fff3e0; color: #b25b00; }
//   .cal-status.st-leave   { background: #f1eafd; color: #6b3fc4; }

//   .legend { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 14px; font-size: 9.5px; }
//   .legend-item { display: inline-flex; align-items: center; gap: 5px; }
//   .legend-code {
//     display: inline-flex; align-items: center; justify-content: center;
//     min-width: 17px; height: 15px; padding: 0 3px;
//     border: 1px solid #d9dce2; border-radius: 4px;
//     font-size: 8.5px; font-weight: 700;
//   }

//   @media print {
//     html, body { background: #ffffff; font-size: 10.5px; }
//     .toolbar { display: none; }
//     .page {
//       margin: 0 auto; max-width: none !important; padding: 4mm 0;
//       box-shadow: none;
//     }
//     /* Only rendered (non-hidden) sections participate in page breaks, so this
//        is a no-op when a single section is being printed on its own. */
//     .print-section + .print-section { break-before: page; page-break-before: always; }

//     /* "Print Salary Slip / Attendance / History" — hide the other two */
//     body[data-print-only="salary"] .print-section:not([data-key="salary"]) { display: none; }
//     body[data-print-only="attendance"] .print-section:not([data-key="attendance"]) { display: none; }
//     body[data-print-only="history"] .print-section:not([data-key="history"]) { display: none; }
//   }
// `;

// const buildSalaryPage = (companyName, printSlip) => `
//   <section class="page print-section" data-key="salary">
//     <div class="brand">${esc(companyName)}</div>
//     <hr class="rule" />
//     <h1 class="title">Salary Slip (${esc(printSlip.dateRangeLabel)})</h1>

//     <table>
//       <tr>
//         <td><div class="info-name">${esc(printSlip.employee.name)}</div></td>
//         <td>
//           <div class="label-small">Phone No</div>
//           <div>${esc(printSlip.employee.mobile || "-")}</div>
//         </td>
//         <td>
//           <div class="label-small">Monthly Gross Salary</div>
//           <div>${inr(printSlip.employee.monthlySalary)}</div>
//         </td>
//       </tr>
//     </table>

//     <h2 class="section-title">Payment &amp; Salary (${esc(printSlip.dateRangeLabel)})</h2>
//     <table>
//       <thead>
//         <tr><th>Earnings</th><th>Activity Date</th><th class="amount">Amount</th></tr>
//       </thead>
//       <tbody>
//         <tr>
//           <td>${esc(printSlip.earningsLabel)}</td>
//           <td>--</td>
//           <td class="amount">${inr(printSlip.netPay)}</td>
//         </tr>
//         <tr class="total-row">
//           <td colspan="2">Total Earnings</td>
//           <td class="amount">${inr(printSlip.netPay)}</td>
//         </tr>
//       </tbody>
//     </table>

//     <table>
//       <tr>
//         <td>Previous Month Closing Balance</td>
//         <td></td>
//         <td class="amount">${inr(printSlip.previousBalance)}</td>
//       </tr>
//       <tr class="total-row">
//         <td>Net Payable (Earnings + Previous Balance)</td>
//         <td>${esc(printSlip.netPayableWords)}</td>
//         <td class="amount">${inr(printSlip.netPayable)}</td>
//       </tr>
//     </table>

//     <h2 class="section-title">Attendance Summary (${esc(printSlip.dateRangeLabel)})</h2>
//     <table class="summary-grid">
//       <tr>
//         <td>Present - ${printSlip.counts.Present}</td>
//         <td>Absent - ${printSlip.counts.Absent}</td>
//         <td>Half Day - ${printSlip.counts["Half-Day"]}</td>
//         <td>Not Marked - ${printSlip.unmarkedDays}</td>
//       </tr>
//       <tr>
//         <td>Overtime - ${printSlip.overtimeMins} Mins</td>
//         <td>Fine - ${printSlip.fineMins} Mins</td>
//         <td>Leaves - ${printSlip.counts.Leave}</td>
//         <td>Payable Days - ${printSlip.payableDays}</td>
//       </tr>
//     </table>
//   </section>
// `;

// const STATUS_CLASS = {
//   Present: "st-present",
//   Absent: "st-absent",
//   "Half-Day": "st-half",
//   Leave: "st-leave",
// };

// const buildAttendancePage = (
//   companyName,
//   employee,
//   statusByDate,
//   gridRows,
//   year,
//   month,
// ) => {
//   const rows = gridRows
//     .map((row) => {
//       const cells = row
//         .map((cell) => {
//           const status = cell.inMonth ? statusByDate[cell.iso] : null;
//           const statusBadge = status
//             ? `<span class="cal-status ${STATUS_CLASS[status] || ""}">${STATUS_CODE[status] || status}</span>`
//             : "";
//           return `
//             <div class="cal-cell${cell.inMonth ? "" : " out-month"}">
//               <div class="cal-day">${cell.day}</div>
//               ${statusBadge}
//             </div>`;
//         })
//         .join("");
//       return cells;
//     })
//     .join("");

//   const legend = STATUS_LEGEND.map(
//     (l) => `
//       <span class="legend-item">
//         <span class="legend-code">${esc(l.code)}</span>${esc(l.label)}
//       </span>`,
//   ).join("");

//   return `
//     <section class="page print-section" data-key="attendance">
//       <div class="brand">${esc(companyName)}</div>
//       <hr class="rule" />

//       <div class="cal-head-row">
//         <div class="cal-month-label">${esc(MONTH_NAMES[month])} ${year}</div>
//         <div class="cal-employee">${esc(employee.name)} &middot; ${esc(employee.staffId || "")}</div>
//       </div>

//       <div class="cal-weekdays">
//         <div class="cal-weekday">Mon</div><div class="cal-weekday">Tue</div><div class="cal-weekday">Wed</div>
//         <div class="cal-weekday">Thu</div><div class="cal-weekday">Fri</div><div class="cal-weekday">Sat</div>
//         <div class="cal-weekday">Sun</div>
//       </div>
//       <div class="cal-grid">${rows}</div>

//       <div class="legend">${legend}</div>
//     </section>
//   `;
// };

// const buildHistoryPage = (companyName, employeeName, employeeHistory) => {
//   const body = employeeHistory.length
//     ? `
//       <table>
//         <thead><tr><th>Change</th><th class="amount">When</th></tr></thead>
//         <tbody>
//           ${employeeHistory
//             .map(
//               (entry) => `
//             <tr>
//               <td>${esc(entry.description)}</td>
//               <td class="amount">${esc(formatDateTime(entry.timestamp))}</td>
//             </tr>`,
//             )
//             .join("")}
//         </tbody>
//       </table>`
//     : `<p class="history-empty">No edits recorded yet.</p>`;

//   return `
//     <section class="page print-section" data-key="history">
//       <div class="brand">${esc(companyName)}</div>
//       <hr class="rule" />
//       <h1 class="title">Edit History &mdash; ${esc(employeeName)}</h1>
//       ${body}
//     </section>
//   `;
// };

// // ─── Toolbar + controller script embedded in the generated document ───────
// const buildToolbar = (defaultSize) => `
//   <div class="toolbar">
//     <div class="size-toggle">
//       <label><input type="radio" name="pageSize" value="A4" ${defaultSize === "A4" ? "checked" : ""} onchange="setPageSize('A4')"><span>A4</span></label>
//       <label><input type="radio" name="pageSize" value="A5" ${defaultSize === "A5" ? "checked" : ""} onchange="setPageSize('A5')"><span>A5</span></label>
//     </div>
//     <button class="primary" onclick="printOnly('all')">Print Full Report</button>
//     <button onclick="printOnly('salary')">Print Salary Slip</button>
//     <button onclick="printOnly('attendance')">Print Attendance</button>
//     <button onclick="printOnly('history')">Print History</button>
//     <div class="spacer"></div>
//     <button class="ghost" onclick="window.close()">Close</button>
//   </div>
// `;

// const CONTROLLER_SCRIPT = `
//   const PAGE_CSS = ${JSON.stringify(PAGE_CSS)};
//   function setPageSize(size) {
//     document.body.setAttribute('data-page-size', size);
//     const styleTag = document.getElementById('page-size-rule');
//     if (styleTag) styleTag.textContent = PAGE_CSS[size] || PAGE_CSS.A4;
//   }
//   function printOnly(key) {
//     document.body.setAttribute('data-print-only', key);
//     window.print();
//   }
//   window.addEventListener('afterprint', function () {
//     document.body.removeAttribute('data-print-only');
//   });
// `;

// /**
//  * Builds the full standalone HTML document (string) for one employee's
//  * Salary Slip + Attendance calendar + History, for the given month.
//  */
// export const buildPrintableSlipHTML = ({
//   employee,
//   records,
//   year,
//   month,
//   history = [],
//   companyName = "Your Company",
//   pageSize = "A4",
// }) => {
//   const printSlip = buildPrintSalarySlip(employee, records, year, month);
//   printSlip.employee = employee; // keep name/mobile/salary alongside the slip numbers

//   const statusByDate = {};
//   records
//     .filter((r) => r.employeeId === employee.id)
//     .forEach((r) => (statusByDate[r.date] = r.status));

//   const gridRows = buildMonthGridWithAdjacent(year, month);

//   const employeeHistory = history
//     .filter((h) => h.employeeId === employee.id)
//     .slice()
//     .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

//   const size = pageSize === "A5" ? "A5" : "A4";

//   return `<!DOCTYPE html>
// <html lang="en">
// <head>
// <meta charset="UTF-8" />
// <title>Salary Slip - ${esc(employee.name)} - ${esc(MONTH_NAMES[month])} ${year}</title>
// <style>${DOCUMENT_STYLES}</style>
// <style id="page-size-rule">${PAGE_CSS[size]}</style>
// </head>
// <body data-page-size="${size}">
//   ${buildToolbar(size)}
//   ${buildSalaryPage(companyName, printSlip)}
//   ${buildAttendancePage(companyName, employee, statusByDate, gridRows, year, month)}
//   ${buildHistoryPage(companyName, employee.name, employeeHistory)}
//   <script>${CONTROLLER_SCRIPT}</script>
// </body>
// </html>`;
// };

// /**
//  * Opens the slip document in a brand new browser window/tab — completely
//  * isolated from the app's own DOM and CSS.
//  *
//  * `options.autoPrint` — "all" | "salary" | "attendance" | "history" — if
//  * set, immediately triggers that print as soon as the window loads (handy
//  * for wiring individual "Print Salary Slip" / "Print Attendance" buttons
//  * directly in the app, instead of making the person click again inside the
//  * popup). Leave it unset to just open the document and let the person pick
//  * from the toolbar.
//  *
//  * `options.pageSize` — "A4" (default) | "A5" — which size the toolbar and
//  * @page rule start on; the person can still switch it inside the popup.
//  *
//  * Returns the window reference (or null if the browser blocked the popup).
//  */
// export const openPrintableSlip = (params, options = {}) => {
//   const { autoPrint = null, pageSize = "A4" } = options;
//   const html = buildPrintableSlipHTML({ ...params, pageSize });
//   const printWindow = window.open("", "_blank", "width=960,height=1040");

//   if (!printWindow) {
//     // Popup blocked — let the caller show a toast/alert if it wants.
//     return null;
//   }

//   printWindow.document.open();
//   printWindow.document.write(html);
//   printWindow.document.close();

//   if (autoPrint) {
//     printWindow.onload = () => {
//       printWindow.focus();
//       printWindow.printOnly(autoPrint);
//     };
//   }

//   return printWindow;
// };
